import { useState, useEffect } from "react";
import { useIsMobile } from "../lib/useIsMobile";
import { getAuthToken, sb, SUPABASE_ANON_KEY, SUPABASE_URL } from "../lib/supabase";
import { formatPhoneInput } from "../lib/format";
import { geocodeAddress } from "../lib/maps";
import { parityFromDate } from "../lib/date";
import { BusinessProfileEditor } from "./BusinessProfileEditor";
import { StripeConnectCard } from "./StripeConnectCard";
import { PlanBillingCard } from "./PlanBillingCard";
import { OperatorPaymentCard } from "./OperatorPaymentCard";
import { BulkImport } from "./BulkImport";
import { TrackingSettings } from "./TrackingSettings";
import { BlockOffDatesEditor } from "./BlockOffDatesEditor";
import { ServiceAreaEditor } from "./ServiceAreaEditor";
import { ServiceAreaMap } from "./ServiceAreaMap";
import { PricingEditor } from "./PricingEditor";
import { WebsiteSettings } from "./WebsiteSettings";
import { FleetSettings } from "./FleetSettings";
import { A2pSettings } from "./A2pSettings";
import { ApiWebhooksSettings } from "./ApiWebhooksSettings";
import { ACTIVE_THEME } from "../themes";
import { SettingsDock } from "./SettingsDock";

export function SettingsPage({ technicians, serviceArea, zipSecondDay, onRefresh, showToast, onGenerateJobs, jobsLoading, clients, ownerEmail, org, onOrgSaved, jump, role, onOpenShop }) {
  const isMobile = useIsMobile();
  const [settingsTab, setSettingsTab] = useState("business");
  const [subTab,      setSubTab]      = useState("profile"); // sub-tab within an overloaded menu
  // Deep-link from a "finish setup" guide (e.g. Call/Text/Fleet) → open that Settings tab.
  useEffect(() => { if (jump && jump.tab) { setSettingsTab(jump.tab); if (jump.sub) setSubTab(jump.sub); } }, [jump]);
  const [newName,     setNewName]     = useState("");
  const [editingTech, setEditingTech] = useState(null); // tech id being edited
  const [editFields,  setEditFields]  = useState({});
  const [newEmail,    setNewEmail]    = useState("");
  const [newPhone,    setNewPhone]    = useState("");
  const [saving,      setSaving]      = useState(false);
  // Change-password (logged-in operator)
  const [pw1,         setPw1]         = useState("");
  const [pw2,         setPw2]         = useState("");
  const [pwBusy,      setPwBusy]      = useState(false);
  const [showAddTech, setShowAddTech] = useState(false); // apple-native: reveal the add form on demand
  const [selfName,    setSelfName]    = useState("");
  const [selfBusy,    setSelfBusy]    = useState(false);
  const [linkCopied,  setLinkCopied]  = useState(false);
  const [codeCopied,  setCodeCopied]  = useState(false);
  const [linkErr,     setLinkErr]     = useState(false);
  const [codeErr,     setCodeErr]     = useState(false);

  // Is the logged-in owner already listed as a technician? Derived from the
  // tech list matching their login email (no extra state to keep in sync).
  const ownerLc  = (ownerEmail||"").trim().toLowerCase();
  const selfTech = ownerLc ? (technicians||[]).find(t => (t.email||"").toLowerCase() === ownerLc) : null;

  // Public signup link + embed snippet. org is a required prop with a non-null,
  // unique slug (generated at operator signup); no fallback needed. Build the
  // absolute URL from window.location.origin so it works on localhost AND prod
  // (there is no site-URL env var). Multi-tenant: slug identifies this org only.
  const signupUrl     = `${window.location.origin}/signup/${encodeURIComponent(org.slug)}`;
  const accepting     = !!(org && org.stripe_account_id && org.onboarding_completed);
  const iframeSnippet = `<iframe src="${signupUrl}" title="Sign up" width="100%" height="900" style="border:0;max-width:520px" loading="lazy"></iframe>`;
  // Mirrors PaymentLinkModal's clipboard idiom: writeText + copied flag + 3s
  // reset, wrapped in try/catch so a non-secure context degrades gracefully.
  async function copyShare(text, setDone, setErr) {
    setErr(false);
    try {
      await navigator.clipboard.writeText(text);
      setDone(true);
      setTimeout(()=>setDone(false), 3000);
    } catch(e) { console.warn("clipboard copy failed", e); setErr(true); }
  }

  async function toggleSelfTech(action) {
    setSelfBusy(true);
    try {
      const res = await sb.fn("toggle-owner-technician",
        action === "add" ? { action, display_name: selfName.trim() } : { action });
      if (res.error) { showToast(res.error, "error"); }
      else if (action === "add")  { setSelfName(""); showToast("You're now listed as a technician"); }
      else { showToast("You're no longer listed as a technician"); }
      await onRefresh();
    } catch(e) { console.error(e); showToast("Could not update technician status","error"); }
    setSelfBusy(false);
  }

  async function addTech() {
    if (!newName.trim()||!newEmail.trim()) return;
    // Guard: don't create a technician whose email is already in use by
    // another technician in this company. (The back-end create-tech-account
    // is the second line of defense for emails belonging to other accounts
    // entirely, but this stops the duplicate row from ever being created.)
    const emailLc = newEmail.trim().toLowerCase();
    if ((technicians||[]).some(t => (t.email||"").toLowerCase() === emailLc)) {
      showToast("A technician with this email already exists.","error");
      return;
    }
    setSaving(true);
    try {
      // Create technician record first
      await fetch(`${SUPABASE_URL}/rest/v1/technicians`,{method:"POST",headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`,"Content-Type":"application/json",Prefer:"return=minimal"},body:JSON.stringify({ name:newName.trim(), email:newEmail.trim(), phone:newPhone.trim()||null, active:true, has_account:true })});
      // Get the new tech id
      const techs = await fetch(`${SUPABASE_URL}/rest/v1/technicians?order=name.asc`,{headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`}}).then(r=>r.json());
      const newTech = Array.isArray(techs) ? techs.find(t=>t.email===newEmail.trim()) : null;
      if (newTech) {
        // Create auth account and email a "set your password" invite link
        // (no plaintext password — the tech sets their own on /reset-password).
        const res = await sb.fn("create-tech-account", { technician_id:newTech.id, name:newName.trim(), email:newEmail.trim(), redirectTo: window.location.origin + "/reset-password" });
        if (res.error) { showToast("Tech added but account creation failed: "+res.error,"error"); }
        else if (res.email_sent === false) { showToast(res.warning || "Tech added, but the invite email failed to send — resend it.","error"); }
        else { showToast(`Invite sent to ${newEmail} to set their password.`); }
      }
      setNewName(""); setNewEmail(""); setNewPhone("");
      await onRefresh();
    } catch(e) { console.error(e); showToast("Error adding technician","error"); }
    setSaving(false);
  }

  // Change-password for the logged-in operator. PUT /auth/v1/user with the
  // current session as Bearer. Supabase may require a recent login for a
  // password change — surface a friendly re-auth hint if it rejects.
  async function changePassword() {
    if (pw1.length < 8) { showToast("Password must be at least 8 characters","error"); return; }
    if (pw1 !== pw2)    { showToast("Passwords do not match","error"); return; }
    setPwBusy(true);
    try {
      const r = await fetch(`${SUPABASE_URL}/auth/v1/user`, {
        method: "PUT",
        headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${getAuthToken()}`, "Content-Type": "application/json" },
        body: JSON.stringify({ password: pw1 }),
      });
      if (r.ok) {
        setPw1(""); setPw2("");
        showToast("Password updated");
      } else {
        const data = await r.json().catch(()=>({}));
        const msg = (data.error_description || data.msg || data.message || "").toLowerCase();
        if (msg.includes("reauth") || msg.includes("recent") || r.status === 401 || r.status === 403) {
          showToast("Please sign out and back in, then try again","error");
        } else {
          showToast(data.error_description || data.msg || data.message || "Could not update password","error");
        }
      }
    } catch(e) { console.error(e); showToast("Could not update password","error"); }
    setPwBusy(false);
  }

  async function toggleTech(tech) {
    await fetch(`${SUPABASE_URL}/rest/v1/technicians?id=eq.${tech.id}`,{method:"PATCH",headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`,"Content-Type":"application/json",Prefer:"return=minimal"},body:JSON.stringify({ active: !tech.active })});
    await onRefresh();
    showToast(`${tech.name} ${!tech.active?"activated":"deactivated"}`);
  }

  // Operator-initiated password reset for a technician. Secure (no plaintext) — we
  // email THEM a set-password link; they choose the new password on /reset-password.
  // Has a login → recovery link; no login yet → an invite that also creates it.
  async function resetTechPassword(t) {
    if (!t.email) { showToast("This technician has no email on file.","error"); return; }
    try {
      if (t.has_account) {
        await sb.fn("request-password-reset", { email: t.email, redirectTo: window.location.origin + "/reset-password" });
        showToast(`Password reset link sent to ${t.email}`);
      } else {
        const res = await sb.fn("create-tech-account", { technician_id: t.id, name: t.name, email: t.email, redirectTo: window.location.origin + "/reset-password" });
        if (res.error) showToast("Couldn't send invite: "+res.error,"error");
        else if (res.email_sent === false) showToast(res.warning || "Invite email failed to send — try again.","error");
        else { showToast(`Set-password invite sent to ${t.email}`); await onRefresh(); }
      }
    } catch(e) { console.error(e); showToast("Couldn't send the reset link","error"); }
  }

  async function removeTech(tech) {
    if (!window.confirm(`Remove ${tech.name} as a technician? This will unassign them from all clients.`)) return;
    // F1: actually unassign (the confirm promised it). Without this the
    // denormalized technician_id on clients/jobs keeps pointing at a deleted tech,
    // so those clients vanish from every route AND never show as unassigned.
    const headers = {apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`,"Content-Type":"application/json",Prefer:"return=minimal"};
    const clearBody = JSON.stringify({technician_id:null,technician_name:null});
    await fetch(`${SUPABASE_URL}/rest/v1/clients?technician_id=eq.${tech.id}`,{method:"PATCH",headers,body:clearBody});
    await fetch(`${SUPABASE_URL}/rest/v1/jobs?technician_id=eq.${tech.id}&status=eq.scheduled`,{method:"PATCH",headers,body:clearBody});
    await fetch(`${SUPABASE_URL}/rest/v1/technicians?id=eq.${tech.id}`,{method:"DELETE",headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`}});
    await onRefresh();
    showToast(`${tech.name} removed — their clients are now unassigned`);
  }

  // apple-native groups Settings into sub-tabs; default shows the full flat list (rollback intact).
  const inTab = (t) => ACTIVE_THEME !== "apple-native" || settingsTab === t;
  // Sub-tabs within the overloaded menus (apple-native only). inSub() is always
  // true off-theme so the flat fallback still renders every section.
  const SUBTABS = {
    business: [["profile", "Profile"], ["plan", "Plan & Payments"], ["security", "Security"]],
    website:  [["site", "Marketing Site"], ["registration", "Registration Form"]],
    team:     [["techs", "Technicians"], ["area", "Service area"], ["blockoff", "Block-off dates"]],
  };
  const inSub = (s) => ACTIVE_THEME !== "apple-native" || subTab === s;
  // Switching the top-level menu resets to that menu's first sub-tab.
  const selectTab = (t) => { setSettingsTab(t); const subs = SUBTABS[t]; setSubTab(subs ? subs[0][0] : null); };
  const canManageApi = !role || role === "owner" || role === "admin";
  const RAIL = [
    ["business", "🏢", "Business", "Profile, plan & payments"],
    ["billing",  "🏷", "Pricing", "Your client rate card"],
    // Add-ons moved to the Shop tab (2026-11-01) with the window notes and Shop banners. The rail
    // keeps a pointer so anyone who looks here for them is sent to the new place, not lost.
    ["addons",   "🧴", "Add-ons", "Moved to the Shop tab"],
    ["website",  "🌐", "Website", "Marketing site & client registration form"],
    ["team",     "👥", "Team & Service", "Technicians, areas & block-offs"],
    ["fleet",    "🚚", "Fleet", "GPS tracking, mileage & proof-of-service"],
    ["texting",  "📱", "Texting", "Two-way client texting & A2P approval"],
    ["data",     "🗂", "Import", "Bulk-import clients from CSV"],
    // API & Webhooks (docs/api-webhooks-spec.md §6) is owner|admin only. App.js already routes
    // user_profiles.role='technician' to the field screen, so Settings is only ever reached by an
    // owner/admin; the `role` prop (optional — App.js may pass userRole) is the belt to that brace.
    // RLS + the manage-* functions enforce the same gate server-side.
    ...(canManageApi ? [["api", "🔌", "API & Webhooks", "Keys, webhooks, event log, docs"]] : []),
  ];

  const body = (
    <>
      {inTab("business") && inSub("profile") && (
        <BusinessProfileEditor org={org} showToast={showToast} onSaved={onOrgSaved}/>
      )}
      {inTab("business") && inSub("plan") && (
      <>
        <div style={{marginBottom:16}}><StripeConnectCard org={org} showToast={showToast}/></div>
        <div className="grid-2" style={{marginBottom:16}}>
          <PlanBillingCard org={org} showToast={showToast} onRefresh={onOrgSaved || onRefresh}/>
          <OperatorPaymentCard org={org} showToast={showToast} onRefresh={onOrgSaved || onRefresh}/>
        </div>
      </>)}
      {inTab("website") && inSub("registration") && (
      <div style={{marginBottom:16}}>
        <div style={{ background:"var(--surface)", border:"1px solid var(--border)", borderRadius:"var(--r-lg, 16px)", boxShadow:"var(--shadow, 0 1px 3px rgba(0,0,0,.06))", overflow:"hidden" }}>

          {/* ── Hero: the OUTCOME + plain-language "what it is" ── */}
          <div style={{ padding:isMobile?"20px 16px":"30px 28px", background:"linear-gradient(135deg, var(--accent-weak), transparent 70%)", borderBottom:"1px solid var(--border)" }}>
            <span style={{ display:"inline-block", fontSize:10.5, fontWeight:800, letterSpacing:".08em", textTransform:"uppercase", color:"var(--accent)", marginBottom:12 }}>📋 On your website</span>
            <div style={{ fontSize:isMobile?21:25, fontWeight:700, letterSpacing:"-.025em", color:"var(--text)", lineHeight:1.14, maxWidth:"20ch" }}>Put a sign-up form right on your own website</div>
            <p style={{ fontSize:14.5, color:"var(--text2)", lineHeight:1.55, margin:"12px 0 0", maxWidth:"50ch" }}>
              Add it to your site and visitors sign up, drop in a card, and get scheduled <strong style={{ color:"var(--text)", fontWeight:600 }}>without ever leaving your page</strong> — each one landing straight in your CRM. No more contact forms that go nowhere.
            </p>
          </div>

          <div style={{ padding:isMobile?"20px 16px 22px":"24px 28px 26px" }}>

            {!accepting && (
              <div style={{ fontSize:12.5, color:"#974f0c", background:"#fffae6", border:"1px solid #ffe7a3", padding:"11px 14px", borderRadius:"var(--r-sm, 8px)", marginBottom:20, lineHeight:1.5 }}>
                ⚠️ <strong>Not live yet.</strong> Connect Stripe + finish onboarding under <strong>Business → Plan &amp; Payments</strong> to start accepting sign-ups. You can still grab your code now.
              </div>
            )}

            {/* ── How it works — 3 plain steps ── */}
            <div style={{ fontSize:11, fontWeight:700, color:"var(--text3)", textTransform:"uppercase", letterSpacing:".06em", marginBottom:16 }}>Add it in 3 steps — no coding</div>
            <div style={{ display:"flex", flexDirection:"column", gap:18 }}>
              {[
                ["1", "Copy your code", "We already built it for you — there's nothing to write.", true],
                ["2", "Paste it on your website", "Drop it into an “Embed” or “Custom HTML” block — the same spot you'd add a photo or video.", false],
                ["3", "You're live", "Save your page and the form appears. Customers can sign up right there.", false],
              ].map(([n, t, d, withCode]) => (
                <div key={n} style={{ display:"flex", gap:13, alignItems:"flex-start" }}>
                  <span style={{ flexShrink:0, width:26, height:26, borderRadius:"50%", background:"var(--accent-weak)", color:"var(--accent)", fontWeight:700, fontSize:13, display:"flex", alignItems:"center", justifyContent:"center" }}>{n}</span>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:14, fontWeight:600, color:"var(--text)" }}>{t}</div>
                    <div style={{ fontSize:12.5, color:"var(--text2)", marginTop:2, lineHeight:1.5 }}>{d}</div>
                    {withCode && (<>
                      <textarea readOnly rows={3} value={iframeSnippet} onFocus={e=>e.target.select()} aria-label="Embed snippet (HTML iframe)"
                        style={{ width:"100%", marginTop:10, padding:"10px 12px", border:"1px solid var(--border)", borderRadius:"var(--r-sm, 8px)", fontSize:12, fontFamily:"ui-monospace,SFMono-Regular,Menlo,monospace", boxSizing:"border-box", resize:"vertical", background:"var(--bg)", color:"var(--text2)" }}/>
                      <button className="btn btn-gold" style={{ marginTop:8 }} aria-label="Copy embed code" onClick={()=>copyShare(iframeSnippet,setCodeCopied,setCodeErr)}>
                        {codeCopied ? "✓ Copied!" : "Copy code"}
                      </button>
                      {codeErr && <div style={{ fontSize:11, color:"#974f0c", marginTop:8 }}>Couldn't copy automatically — select the code above and copy it manually.</div>}
                    </>)}
                  </div>
                </div>
              ))}
            </div>

            {/* ── Reassurance strip ── */}
            <div style={{ marginTop:20, padding:"14px 16px", background:"var(--bg)", border:"1px solid var(--border)", borderRadius:"var(--r, 12px)", display:"flex", flexDirection:"column", gap:8, fontSize:12.5, color:"var(--text2)", lineHeight:1.45 }}>
              <div>✅ <strong style={{ color:"var(--text)", fontWeight:600 }}>No coding needed</strong> — if you can copy &amp; paste, you can do this.</div>
              <div>🧩 Works with Squarespace, Wix, WordPress, GoDaddy &amp; Weebly.</div>
              <div>🙋 Not sure where it goes? Send the code to whoever built your site — they'll know exactly what to do.</div>
              <div>🔄 Change your prices or services later? Your form updates itself — no need to re-paste.</div>
            </div>

            <div style={{ borderTop:"1px solid var(--border)", margin:"24px 0" }} />

            {/* ── No website? Just share the hosted link ── */}
            <div style={{ fontSize:11, fontWeight:700, color:"var(--text3)", textTransform:"uppercase", letterSpacing:".06em", marginBottom:4 }}>No website? Just share a link</div>
            <div style={{ fontSize:12.5, color:"var(--text2)", marginBottom:8 }}>It's the same form, hosted by us. Text it to a lead, or drop it in your Instagram bio, Google Business, or a flyer's QR code.</div>
            <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap" }}>
              <input readOnly value={signupUrl} onFocus={e=>e.target.select()} aria-label="Public signup link"
                style={{ flex:1, minWidth:240, padding:"11px 13px", border:"1px solid var(--border)", borderRadius:"var(--r-sm, 8px)", fontSize:13.5, fontWeight:500, boxSizing:"border-box", background:"var(--bg)", color:"var(--text)" }}/>
              <button className="btn btn-ghost" aria-label="Copy signup link" onClick={()=>copyShare(signupUrl,setLinkCopied,setLinkErr)}>
                {linkCopied ? "✓ Copied!" : "Copy link"}
              </button>
              {accepting && <a className="btn btn-ghost" href={signupUrl} target="_blank" rel="noopener noreferrer">Preview ↗</a>}
            </div>
            {linkErr && (
              <div style={{ fontSize:11, color:"#974f0c", marginTop:8 }}>Couldn't copy automatically — select the link above and copy it manually.</div>
            )}
          </div>
        </div>
        <TrackingSettings org={org} showToast={showToast} onSaved={onOrgSaved || onRefresh}/>
      </div>)}
      {inTab("business") && inSub("security") && (
      <div style={{marginBottom:16}}>
        <div className="card">
          <div className="card-title">🔑 Change Password</div>
          <div style={{fontSize:12,color:"var(--text2)",marginBottom:14}}>Update the password for your iScoopCRM login.</div>
          <div className="form-grid">
            <div className="form-group">
              <label>New password</label>
              <input type="password" value={pw1} onChange={e=>setPw1(e.target.value)} placeholder="Min 8 characters" autoComplete="new-password"/>
            </div>
            <div className="form-group">
              <label>Confirm new password</label>
              <input type="password" value={pw2} onChange={e=>setPw2(e.target.value)} placeholder="••••••••" autoComplete="new-password"/>
            </div>
          </div>
          <button className="btn btn-gold" style={{marginTop:10}} onClick={changePassword} disabled={pwBusy||!pw1||!pw2}>
            {pwBusy?"Updating...":"Update password"}
          </button>
          {/* A .45-opacity disabled button doesn't read as disabled on a phone — say what's missing. */}
          {isMobile && !pwBusy && (!pw1 || !pw2) && (
            <div style={{fontSize:11.5,color:"var(--text2)",marginTop:8}}>Enter and confirm a new password to continue.</div>
          )}
        </div>
      </div>)}
      {inTab("team") && inSub("techs") && (
        <div className="card">
          <div className="card-title">👤 Technicians</div>
          <div style={{marginBottom:16}}>
            {(technicians||[]).map(t=>(
              <div key={t.id} style={{borderBottom:"1px solid var(--border)"}}>
                {/* Normal row */}
                {/* Phones: the row wraps and the four actions drop to their own line —
                    inline they need ~450px and the right-hand ones were clipped off-screen. */}
                <div style={{display:"flex",alignItems:"center",gap:10,padding:"10px 0",flexWrap:isMobile?"wrap":"nowrap"}}>
                  <div className="amono" style={{width:36,height:36,fontSize:14,background:t.active?"var(--accent-weak)":"var(--border)",color:t.active?"var(--accent)":"var(--text3)"}}>
                    {t.name[0].toUpperCase()}
                  </div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontWeight:600,fontSize:13,color:t.active?"var(--text)":"var(--text2)"}}>{t.name}</div>
                    <div style={{fontSize:11,color:"var(--text2)"}}>{t.email||"No email"} {t.phone?`· ${t.phone}`:""}</div>
                    <div style={{display:"flex",gap:10,marginTop:4}}>
                      <span style={{fontSize:11,color:"var(--text2)"}}>Rate: <strong style={{color:"var(--text)"}}>${t.hourly_rate||0}/hr</strong></span>
                      <span style={{fontSize:11,color:"var(--text2)"}}>Default hrs: <strong style={{color:"var(--text)"}}>{t.default_hours||0}</strong></span>
                    </div>
                    {!t.has_account&&t.email&&<span className="badge badge-gold" style={{marginTop:2,display:"inline-block"}}>No login</span>}
                    {t.has_account&&<span className="badge badge-green" style={{marginTop:2,display:"inline-block"}}>✓ Has login</span>}
                  </div>
                  <span className={`badge ${t.active?"badge-green":"badge-gray"}`}>{t.active?"Active":"Inactive"}</span>
                  <div style={isMobile?{flexBasis:"100%",display:"flex",gap:6,flexWrap:"wrap",paddingLeft:46}:{display:"contents"}}>
                    <button className="btn btn-ghost btn-sm" onClick={()=>{
                      if (editingTech===t.id) { setEditingTech(null); setEditFields({}); }
                      else { setEditingTech(t.id); setEditFields({name:t.name,email:t.email||"",phone:t.phone||""}); }
                    }}>{editingTech===t.id?"Cancel":"✏️ Edit"}</button>
                    {t.email && <button className="btn btn-ghost btn-sm" onClick={()=>resetTechPassword(t)}>{t.has_account?"🔑 Reset password":"✉️ Send invite"}</button>}
                    <button className="btn btn-ghost btn-sm" onClick={()=>toggleTech(t)}>{t.active?"Deactivate":"Activate"}</button>
                    <button className="btn btn-sm" style={{background:"rgba(215,50,43,.10)",color:"var(--danger)",border:"none"}} onClick={()=>removeTech(t)}>Remove</button>
                  </div>
                </div>

                {/* Inline edit form */}
                {editingTech===t.id&&(
                  <div style={{background:"var(--bg)",borderRadius:8,padding:14,marginBottom:10,border:"1px solid var(--border)"}}>
                    <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))",gap:10,marginBottom:10}}>
                      <div>
                        <div style={{fontSize:11,color:"var(--text2)",marginBottom:4}}>NAME</div>
                        <input value={editFields.name||""} onChange={e=>setEditFields(f=>({...f,name:e.target.value}))}
                          style={{width:"100%",border:"1px solid var(--border)",borderRadius:6,padding:"6px 10px",fontSize:13,boxSizing:"border-box"}}/>
                      </div>
                      <div>
                        <div style={{fontSize:11,color:"var(--text2)",marginBottom:4}}>EMAIL</div>
                        <input value={editFields.email||""} onChange={e=>setEditFields(f=>({...f,email:e.target.value}))}
                          style={{width:"100%",border:"1px solid var(--border)",borderRadius:6,padding:"6px 10px",fontSize:13,boxSizing:"border-box"}}/>
                      </div>
                      <div>
                        <div style={{fontSize:11,color:"var(--text2)",marginBottom:4}}>PHONE</div>
                        <input value={editFields.phone||""} onChange={e=>setEditFields(f=>({...f,phone:formatPhoneInput(e.target.value)}))} inputMode="tel"
                          style={{width:"100%",border:"1px solid var(--border)",borderRadius:6,padding:"6px 10px",fontSize:13,boxSizing:"border-box"}}/>
                      </div>
                    </div>
                    {editFields.name && editFields.name !== t.name && (
                      <div style={{fontSize:11,color:"#974f0c",background:"#fffae6",padding:"6px 10px",borderRadius:6,marginBottom:10}}>
                        ⚠️ Renaming will update this tech's name in clients, jobs, trips, and vehicles.
                      </div>
                    )}
                    <div style={{display:"flex",gap:8}}>
                      <button className="btn btn-gold btn-sm" onClick={async()=>{
                        const nameChanged = editFields.name && editFields.name.trim() !== t.name;
                        // Update technician record
                        await fetch(`${SUPABASE_URL}/rest/v1/technicians?id=eq.${t.id}`,{
                          method:"PATCH",
                          headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`,"Content-Type":"application/json",Prefer:"return=minimal"},
                          body:JSON.stringify({name:editFields.name?.trim()||t.name, email:editFields.email?.trim()||null, phone:editFields.phone?.trim()||null})
                        });
                        // If name changed, sync everywhere
                        if (nameChanged) {
                          const newName = editFields.name.trim();
                          await fetch(`${SUPABASE_URL}/rest/v1/clients?technician_id=eq.${t.id}`,{method:"PATCH",headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`,"Content-Type":"application/json",Prefer:"return=minimal"},body:JSON.stringify({technician_name:newName})});
                          await fetch(`${SUPABASE_URL}/rest/v1/jobs?technician_id=eq.${t.id}`,{method:"PATCH",headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`,"Content-Type":"application/json",Prefer:"return=minimal"},body:JSON.stringify({technician_name:newName})});
                          await fetch(`${SUPABASE_URL}/rest/v1/trips?technician_id=eq.${t.id}`,{method:"PATCH",headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`,"Content-Type":"application/json",Prefer:"return=minimal"},body:JSON.stringify({technician_name:newName})});
                          await fetch(`${SUPABASE_URL}/rest/v1/vehicles?technician_id=eq.${t.id}`,{method:"PATCH",headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`,"Content-Type":"application/json",Prefer:"return=minimal"},body:JSON.stringify({technician_name:newName})});
                          showToast(`${t.name} renamed to ${newName} — synced everywhere`);
                        } else {
                          showToast("Tech updated");
                        }
                        setEditingTech(null); setEditFields({});
                        onRefresh();
                      }}>✓ Apply Changes</button>
                      <button className="btn btn-ghost btn-sm" onClick={()=>{setEditingTech(null);setEditFields({});}}>Cancel</button>
                    </div>
                  </div>
                )}
              </div>
            ))}
            {(!technicians||technicians.length===0)&&<div className="empty"><p>No technicians yet</p></div>}
          </div>

          {/* Owner: opt in/out of also being a technician */}
          {ownerEmail && (
            <div style={{borderTop:"1px solid var(--border)",paddingTop:16,marginBottom:4}}>
              {selfTech ? (
                <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
                  <span style={{fontSize:13,color:"var(--text)"}}>✓ You're listed as a technician (<strong>{selfTech.name}</strong>) and can be put on routes.</span>
                  <button className="btn btn-ghost btn-sm" disabled={selfBusy} onClick={()=>toggleSelfTech("remove")}>
                    {selfBusy?"…":"Remove myself"}
                  </button>
                </div>
              ) : (
                <div>
                  <div style={{fontWeight:600,fontSize:13,color:"var(--text)",marginBottom:8}}>Work routes yourself?</div>
                  <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
                    <input value={selfName} onChange={e=>setSelfName(e.target.value)} placeholder="Your display name (e.g. Luis)" style={{flex:1,minWidth:180,padding:"8px 10px",border:"1px solid var(--border)",borderRadius:6,fontSize:13}}/>
                    <button className="btn btn-ghost btn-sm" disabled={selfBusy||!selfName.trim()} onClick={()=>toggleSelfTech("add")}>
                      {selfBusy?"…":"➕ Add myself as a technician"}
                    </button>
                  </div>
                  <div style={{fontSize:11,color:"var(--text2)",marginTop:4}}>Adds you to the technician list using your owner login — no separate account.</div>
                </div>
              )}
            </div>
          )}

          {ACTIVE_THEME === "apple-native" && !showAddTech ? (
            <div style={{borderTop:"1px solid var(--border)",paddingTop:16}}>
              <button className="btn btn-ghost" onClick={()=>setShowAddTech(true)}>+ Add technician</button>
            </div>
          ) : (
          <div style={{borderTop:"1px solid var(--border)",paddingTop:16}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
              <div style={{fontWeight:600,fontSize:13,color:"var(--text)"}}>Add Technician</div>
              {ACTIVE_THEME === "apple-native" && <button className="btn btn-ghost btn-sm" onClick={()=>setShowAddTech(false)}>Cancel</button>}
            </div>
            <div className="form-grid">
              <div className="form-group full">
                <label>Name *</label>
                <input value={newName} onChange={e=>setNewName(e.target.value)} placeholder="Full name"/>
              </div>
              <div className="form-group">
                <label>Email *</label>
                <input type="email" value={newEmail} onChange={e=>setNewEmail(e.target.value)} placeholder="tech@email.com"/>
              </div>
              <div className="form-group">
                <label>Phone</label>
                <input value={newPhone} onChange={e=>setNewPhone(formatPhoneInput(e.target.value))} inputMode="tel" placeholder="555-555-5555"/>
              </div>
            </div>
            <div style={{fontSize:11,color:"var(--text2)",marginTop:8}}>We'll email them a secure link to set their own password — no temporary password needed.</div>
            <button className="btn btn-gold" style={{marginTop:10}} onClick={addTech} disabled={saving||!newName.trim()||!newEmail.trim()}>
              {saving?"Sending invite...":"+ Add Technician & Send Invite"}
            </button>
            {isMobile && !saving && (!newName.trim() || !newEmail.trim()) && (
              <div style={{fontSize:11.5,color:"var(--text2)",marginTop:8}}>Name and email are required.</div>
            )}
          </div>
          )}
        </div>
      )}
      {inTab("team") && inSub("area") && (
        <>
          <ServiceAreaMap serviceArea={serviceArea} clients={clients} onRefresh={onRefresh} showToast={showToast}/>
          <ServiceAreaEditor serviceArea={serviceArea} zipSecondDay={zipSecondDay} onRefresh={onRefresh} showToast={showToast}/>
        </>
      )}
      {inTab("team") && inSub("blockoff") && (
        <div className="card">
          <div className="card-title">🚫 Block Off Dates</div>
          <div style={{fontSize:12,color:"var(--text2)",marginBottom:14,lineHeight:1.6}}>
            Mark dates where no service will be provided — holidays, vacations, weather days, etc. These are flagged on the schedule so jobs never land on them.
          </div>
          <BlockOffDatesEditor showToast={showToast} onRefresh={onRefresh}/>
        </div>
      )}

      {/* ── Bulk CSV Import ── */}
      {inTab("data") && (<div className="card" style={{marginTop:20}}>
        <div className="card-title">📥 Bulk Import Clients from CSV</div>
        <div style={{fontSize:13,color:"var(--text2)",lineHeight:1.6,margin:"2px 0 16px",maxWidth:720}}>
          Bring your whole client base over at once. Clients import as <strong style={{color:"var(--text)"}}>Payment Pending</strong> and
          addresses geocode automatically — then assign a technician and send a payment link from each profile.
          <div style={{marginTop:10}}>
            <strong style={{color:"var(--text)"}}>Two files (recommended):</strong> drop your Client List + Subscriptions exports and we join them by
            address, reading each client's dogs &amp; cadence from the plan name (e.g. <em>Regular Plan&nbsp;-&nbsp;2d-1xW</em>). One combined sheet works too.
          </div>
          <div style={{marginTop:6}}>
            <strong style={{color:"var(--text)"}}>One file:</strong> just upload your Client List — everyone imports, flagged “no plan” until you set
            dogs &amp; frequency by hand on each profile.
          </div>
          <div style={{marginTop:10}}>
            <strong style={{color:"var(--text)"}}>Keep everyone on their current rhythm:</strong> add a <strong style={{color:"var(--text)"}}>Last Service Date</strong> column
            (their most recent cleanup) and, if they get deodorizing, a <strong style={{color:"var(--text)"}}>Last Deodorizer Date</strong>. Without them we can't tell which of the
            two alternating weeks a bi-weekly client is on, or which week of the month their deodorizer falls in — so they'd restart on our guess instead of theirs.
            You can also set both later on any client's profile.
          </div>
          <div style={{marginTop:10,fontSize:12,color:"var(--text3)"}}>
            Needs Name, Email, Address, Zip · optional Phone, City, State, Frequency, Dogs, Deodorizer Frequency, Last Service Date, Last Deodorizer Date, SMS · or grab the <strong style={{color:"var(--text2)"}}>Template CSV</strong> below.
          </div>
        </div>
        <BulkImport onImport={async(newClients)=>{
          let imported = 0, skipped = 0, failed = 0;
          // Fetch existing clients from DB to check for real duplicates
          const existingClients = await fetch(`${SUPABASE_URL}/rest/v1/clients?select=email,address,zip`,{
            headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`}
          }).then(r=>r.json()).catch(()=>[]);
          const existingEmails    = new Set((existingClients||[]).map((c:any)=>c.email?.toLowerCase()).filter(Boolean));
          const existingAddresses = new Set((existingClients||[]).map((c:any)=>`${c.address?.toLowerCase()}|${c.zip}`).filter(Boolean));
          // Resolve technician name → id from the org's technicians (matched on first name, case-insensitive)
          const techByName:any = {};
          (technicians||[]).forEach((t:any)=>{ const k=(t.name||"").trim().toLowerCase().split(/\s+/)[0]; if(k) techByName[k]=t; });

          for (const c of newClients) {
            try {
              // Skip real duplicates
              if (c.email && existingEmails.has(c.email.toLowerCase())) { skipped++; continue; }
              if (c.address && c.zip && existingAddresses.has(`${c.address.toLowerCase()}|${c.zip}`)) { skipped++; continue; }
              const tech = c.tech ? techByName[c.tech.toLowerCase().split(/\s+/)[0]] : null;
              // Geocode on import (runs in the browser, so the referrer-restricted Maps
              // key works) — same as the New-Client form does. Best-effort: a miss just
              // leaves coords null (the Route tab has a backfill for those).
              let geo = null;
              if (c.address && c.zip) { try { geo = await geocodeAddress(c.address, c.zip); } catch(_e) { geo = null; } }
              const res = await fetch(`${SUPABASE_URL}/rest/v1/clients`,{
                method:"POST",
                headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`,"Content-Type":"application/json",Prefer:"return=minimal"},
                body:JSON.stringify({
                  name:c.name, first_name:c.first_name||null, last_name:c.last_name||null,
                  email:c.email||null, phone:c.phone||null,
                  address:c.address||null, city:c.city||null, state:c.state||null, zip:c.zip||null,
                  lat: geo?.lat ?? null, lng: geo?.lng ?? null,
                  location_source: geo ? "google" : null, location_type: geo?.location_type ?? null,
                  geocoded_at: geo ? new Date().toISOString() : null,
                  frequency:c.frequency||null,
                  dogs:(c.dogs!=null?c.dogs:null),
                  technician_id: tech?.id || null,
                  technician_name: tech?.name || c.tech || null,
                  deo_enabled: !!c.deoFreq,
                  deo_frequency: c.deoFreq || null,
                  deo_status: c.deoFreq ? "payment_pending" : null,
                  // Fortnight is taken from a REAL past visit when the export carried one.
                  // With no date it stays NULL — explicitly "not known yet" — and the
                  // Schedule banner surfaces it, rather than a silent 'even' guess that's
                  // wrong half the time and never self-corrects.
                  biweekly_parity: ((c.frequency === "biweekly" || c.deoFreq === "biweekly") && c.lastServiceDate)
                    ? parityFromDate(c.lastServiceDate) : null,
                  // Kept so the profile can SHOW what the fortnight was derived from, and so
                  // the operator can correct it there without re-importing.
                  last_service_date: c.lastServiceDate || null,
                  // Pins a MONTHLY deo to the client's own cycle. NULL falls back to the
                  // calendar rule, and the first completed deo rolls it automatically.
                  deo_anchor_date: (c.deoFreq && c.lastDeoDate) ? c.lastDeoDate : null,
                  sms_consent: !!c.smsConsent,
                  sms_consent_at: c.smsConsent ? new Date().toISOString() : null,
                  notes: c.serviceDay ? `Imported from prior CRM. Route day: ${c.serviceDay}.` : null,
                  active:false, status:"payment_pending",
                  start_date:new Date().toISOString().split("T")[0]
                })
              });
              if (res.ok) {
                imported++;
                // Track for duplicate check within this batch
                if (c.email) existingEmails.add(c.email.toLowerCase());
                if (c.address&&c.zip) existingAddresses.add(`${c.address.toLowerCase()}|${c.zip}`);
              } else {
                const err = await res.text();
                console.error("Failed to import:", c.name, err);
                failed++;
              }
            } catch(e) { console.error("Failed to import:", c.name, e); failed++; }
          }
          await onRefresh();
          const msg = `Imported ${imported} clients${skipped?` (${skipped} duplicates skipped)`:""}${failed?` (${failed} failed)`:""}`;
          showToast(msg, failed>0?"error":undefined);
          // Silently re-match trips in background against newly imported clients
          if (imported > 0) {
            (async()=>{
              let offset = 0; let done = false;
              while (!done) {
                const res = await fetch(`${SUPABASE_URL}/functions/v1/rematch-trips`,{
                  method:"POST",
                  headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`,"Content-Type":"application/json"},
                  body:JSON.stringify({ offset })
                }).then(r=>r.json()).catch(()=>({ done:true }));
                offset = res.offset || 0; done = res.done || true;
              }
            })();
          }
        }} showToast={showToast}/>
      </div>)}

      {/* Pricing Editor */}
      {inTab("billing") && <PricingEditor showToast={showToast} onRefresh={onRefresh} clients={clients}/>}
      {inTab("addons") && (
        <div className="card">
          <div className="card-title">🛍 Add-ons moved to Shop</div>
          <div style={{fontSize:13.5,color:"var(--text2)",lineHeight:1.55,maxWidth:560}}>
            Everything clients see for deals and extras now lives in one place — the <strong style={{color:"var(--text)"}}>Shop</strong> tab in the menu:
            your add-ons, the notes on clients' app windows, and the banners in their Shop.
          </div>
          {onOpenShop && <button className="btn btn-primary" style={{marginTop:14}} onClick={()=>onOpenShop("addons")}>Open Shop → Add-ons</button>}
        </div>
      )}

      {inTab("fleet") && <FleetSettings showToast={showToast}/>}

      {inTab("texting") && <A2pSettings org={org} showToast={showToast}/>}

      {canManageApi && inTab("api") && <ApiWebhooksSettings org={org} showToast={showToast}/>}

      {inTab("website") && inSub("site") && <WebsiteSettings org={org} ownerEmail={ownerEmail} clients={clients} showToast={showToast}/>}
    </>
  );

  // default: original flat scroll (rollback intact).
  // apple-native: a macOS-style settings layout — left category rail + roomy content pane.
  if (ACTIVE_THEME !== "apple-native") return <div>{body}</div>;
  const active = RAIL.find(r => r[0] === settingsTab) || RAIL[0];
  // The Website tab is a full "studio" (form + live preview) — let it break out of the
  // 760px settings pane and own its own layout instead of the flattened card sections.
  const isWebsite = settingsTab === "website" && subTab === "site";
  return (
    <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: isMobile ? 16 : 28, alignItems: isMobile ? "stretch" : "flex-start", minWidth: 0 }}>
      <SettingsDock
        items={RAIL.map(([key, icon, label, desc]) => ({ key, icon, label, desc }))}
        active={settingsTab}
        onSelect={selectTab}
      />
      <div style={{ flex: 1, minWidth: 0, maxWidth: isWebsite ? 1180 : 760 }}>
        <div style={{ fontSize: 24, fontWeight: 600, letterSpacing: "-.025em", marginBottom: 4, paddingLeft: 2 }}>{active[2]}</div>
        <div style={{ fontSize: 13.5, color: "var(--text2)", marginBottom: SUBTABS[settingsTab] ? 16 : 22, paddingLeft: 2 }}>{active[3]}</div>
        {SUBTABS[settingsTab] && (
          // Phones: .seg is width:fit-content and the Team tab's three labels need ~400px,
          // so the last sub-tab was clipped and untappable. Make it a full-width strip that
          // scrolls sideways (no wrap, no clipping) instead.
          <div className="seg" style={isMobile
            ? { marginBottom: 18, display: "flex", width: "100%", maxWidth: "100%", overflowX: "auto", WebkitOverflowScrolling: "touch", scrollbarWidth: "none" }
            : { marginBottom: 22 }}>
            {SUBTABS[settingsTab].map(([k, label]) => (
              <button key={k} className={subTab === k ? "on" : ""} onClick={() => setSubTab(k)}
                style={isMobile ? { whiteSpace: "nowrap", flexShrink: 0, padding: "9px 13px", minHeight: 40 } : undefined}>{label}</button>
            ))}
          </div>
        )}
        {isWebsite ? body : <div className="settings-pane">{body}</div>}
      </div>
    </div>
  );
}
